import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Leaf, MapPin, Repeat } from "lucide-react";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";
import ContactSection from "../components/ContactSection";

const BLOCKS = [
  { id: "matieres", icon: Leaf },
  { id: "fabrication", icon: MapPin },
  { id: "rythme", icon: Repeat },
];

function EditorialSection({ label, paragraphs, testid }) {
  return (
    <section data-testid={testid} className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16">
        <Reveal className="md:col-span-4">
          <p className="text-[11px] tracking-[0.32em] uppercase text-[#5C4033]">{label}</p>
        </Reveal>
        <div className="md:col-span-7 md:col-start-6 space-y-6">
          {paragraphs.map((p, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <p className="text-base md:text-lg leading-[1.9] text-[#4A4641] max-w-2xl">{p}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function APropos() {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#contact") {
      const timer = setTimeout(() => {
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  return (
    <main data-testid="apropos-page" className="relative pt-32 md:pt-40 pb-24 bg-[#FBF8EF]">
      <SEO
        title="À propos"
        description="Pulaar est un vestiaire contemporain né entre Paris et Dakar. La marque puise dans l'histoire, les cultures et les savoir-faire de l'Afrique de l'Ouest."
        path="/a-propos"
      />

      {/* CONTACT — en tête de page */}
      <ContactSection />

      {/* À PROPOS — en dessous */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16 mt-32 md:mt-48 mb-28 md:mb-40 border-t border-[#E5DFD3] pt-24 md:pt-32">
        <Reveal>
          <p className="text-[11px] tracking-[0.32em] uppercase text-[#5C4033] mb-5">
            {t("apropos.eyebrow")}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-serif text-[40px] md:text-[68px] lg:text-[88px] leading-[0.95] tracking-[-0.02em] text-[#121212] max-w-[14ch]">
            Pulaar.
          </h1>
        </Reveal>
      </section>

      {/* 01 — PULAAR */}
      <div className="space-y-28 md:space-y-40">
        <EditorialSection
          testid="apropos-pulaar"
          label={t("apropos.s1Label")}
          paragraphs={[t("apropos.s1p1"), t("apropos.s1p2"), t("apropos.s1p3")]}
        />

        {/* 02 — LE NOM */}
        <EditorialSection
          testid="apropos-nom"
          label={t("apropos.s2Label")}
          paragraphs={[t("apropos.s2p1"), t("apropos.s2p2"), t("apropos.s2p3")]}
        />

        {/* 03 — LE VESTIAIRE */}
        <EditorialSection
          testid="apropos-vestiaire"
          label={t("apropos.s3Label")}
          paragraphs={[t("apropos.s3p1"), t("apropos.s3p2")]}
        />
      </div>

      {/* Phrase isolée */}
      <section
        data-testid="apropos-phrase"
        className="mx-auto max-w-[1100px] px-6 md:px-12 py-32 md:py-48 text-center"
      >
        <Reveal>
          <p className="font-serif italic text-2xl md:text-4xl leading-[1.35] text-[#121212] text-balance">
            {t("apropos.phraseA")}
            <br />
            {t("apropos.phraseB")}
          </p>
        </Reveal>
      </section>

      {/* MATIÈRES · FABRICATION · RYTHME */}
      <section
        data-testid="apropos-blocks"
        className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-8 md:pt-12"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 lg:gap-x-16 gap-y-16">
          {BLOCKS.map((b, i) => {
            const Icon = b.icon;
            return (
              <Reveal key={b.id} delay={i * 0.08}>
                <div data-testid={`apropos-block-${b.id}`} className="border-t border-[#E5DFD3] pt-8">
                  <Icon size={22} strokeWidth={1.1} className="text-[#5C4033] mb-7" />
                  <h3 className="font-serif text-2xl md:text-3xl text-[#121212] tracking-[-0.01em]">
                    {t(`apropos.blocks.${b.id}.title`)}
                  </h3>
                  <p className="mt-5 text-sm md:text-[15px] leading-[1.85] text-[#4A4641] max-w-sm">
                    {t(`apropos.blocks.${b.id}.text`)}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>
    </main>
  );
}
